import { useCallback, useEffect, useRef, useState } from 'react';
import type { ArtDraftSpecAsset } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';

/**
 * The Library's add-asset flow (crucible#2611 Task 6): describe a new asset,
 * review the drafted spec, register it, and immediately generate its first
 * take (crucible#3163). A failed first generation retries continuously with
 * capped backoff while the dialog stays open. Closing after registration
 * preserves and selects the pending asset so the inspector can recover it.
 *
 * crucible#2868 AC-3: `initialCategory` lets a caller opening this from a
 * specific Library category section (e.g. clicking "+ Add prop" while
 * viewing the "prop" chip) pre-select that category, so adding a same-type
 * asset doesn't require re-picking it from the dropdown. Exported so
 * `ArtLibrary` can validate a chip id against the same list before treating
 * it as an addable category, instead of a second hand-maintained copy.
 */

type ArtRedoResult = { ok: true } | { ok: false; error: string };

const FIRST_GENERATION_RETRY_MS = [1_000, 2_000, 5_000, 10_000] as const;
const MAX_FIRST_GENERATION_RETRY_MS = 15_000;

function firstGenerationRetryDelay(failedAttempts: number): number {
  return FIRST_GENERATION_RETRY_MS[failedAttempts - 1] ?? MAX_FIRST_GENERATION_RETRY_MS;
}

export const CATEGORIES = [
  'prop',
  'sprite',
  'background',
  'portrait',
  'ui',
  'shape',
  'moodboard',
  'audio',
] as const;

export interface AddAssetDialogProps {
  gameId: string;
  onClose: () => void;
  /** Fires after a successful `art:add-asset`, with the newly-registered
   *  asset's (possibly caller-edited) id. */
  onAdded: (id: string) => void;
  /** Pre-select this category (crucible#2868 AC-3) instead of the 'prop'
   *  default. Ignored (falls back to 'prop') if it isn't one of
   *  {@link CATEGORIES} — a caller should validate first, but the dialog
   *  never trusts an unchecked value into its own state. */
  initialCategory?: string;
}

export function AddAssetDialog({
  gameId,
  onClose,
  onAdded,
  initialCategory,
}: AddAssetDialogProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [category, setCategory] = useState<string>(
    initialCategory && (CATEGORIES as readonly string[]).includes(initialCategory)
      ? initialCategory
      : 'prop',
  );
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState<ArtDraftSpecAsset | null>(null);
  const [busy, setBusy] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registeredId, setRegisteredId] = useState<string | null>(null);
  const [generationAttempt, setGenerationAttempt] = useState(0);
  const [nextRetryMs, setNextRetryMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const registeredIdRef = useRef<string | null>(null);
  const wakeRetryRef = useRef<(() => void) | null>(null);

  const waitForRetry = useCallback(
    (delayMs: number) =>
      new Promise<void>((resolve) => {
        let settled = false;
        const finish = (): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (wakeRetryRef.current === finish) wakeRetryRef.current = null;
          resolve();
        };
        const timer = setTimeout(finish, delayMs);
        wakeRetryRef.current = finish;
      }),
    [],
  );

  useEffect(
    () => () => {
      cancelledRef.current = true;
      wakeRetryRef.current?.();
    },
    [],
  );

  const onDraft = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await client.invoke('art:draft-spec', {
        gameId,
        category,
        description,
      });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      setDraft(result.draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [client, gameId, category, description]);

  const onAdd = useCallback(async () => {
    if (!draft) return;
    cancelledRef.current = false;
    setBusy(true);
    setRegistering(true);
    setError(null);
    try {
      const result = await client.invoke('art:add-asset', {
        gameId,
        draft,
      });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      registeredIdRef.current = draft.id;
      setRegisteredId(draft.id);
      setRegistering(false);

      let failedAttempts = 0;
      while (!cancelledRef.current) {
        const attempt = failedAttempts + 1;
        setGenerationAttempt(attempt);
        setNextRetryMs(null);
        setError(null);

        let generationResult: ArtRedoResult;
        try {
          generationResult = await client.invoke('art:redo', {
            gameId,
            id: draft.id,
            feedback: '',
          });
        } catch (err) {
          generationResult = {
            ok: false,
            error: err instanceof Error ? err.message : String(err),
          };
        }

        if (cancelledRef.current) return;
        if (generationResult.ok) {
          onAdded(draft.id);
          return;
        }

        failedAttempts += 1;
        const delayMs = firstGenerationRetryDelay(failedAttempts);
        setError(generationResult.error);
        setNextRetryMs(delayMs);
        await waitForRetry(delayMs);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!cancelledRef.current) {
        setBusy(false);
        setRegistering(false);
      }
    }
  }, [client, gameId, draft, onAdded, waitForRetry]);

  const onDialogClose = useCallback(() => {
    if (registering) return;
    cancelledRef.current = true;
    wakeRetryRef.current?.();
    const pendingId = registeredIdRef.current;
    if (pendingId) {
      onAdded(pendingId);
      return;
    }
    onClose();
  }, [onAdded, onClose, registering]);

  return (
    <div
      className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      data-testid="add-asset-dialog"
    >
      <div className="w-full max-w-md rounded-xl border border-white/10 bg-crucible-bg p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white/90">Add a new asset</h2>
          <button
            type="button"
            onClick={onDialogClose}
            disabled={registering}
            data-testid="add-asset-dialog-close"
            className="text-white/40 transition hover:text-white/70 disabled:opacity-30"
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="add-asset-dialog-error"
            className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
          >
            {error}
          </div>
        )}

        {registeredId && (
          <div
            role="status"
            data-testid="add-asset-dialog-generation-status"
            className="mb-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70"
          >
            {nextRetryMs === null ? (
              <p>Generating first version (attempt {generationAttempt})…</p>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p>
                  Generation attempt {generationAttempt} failed. Retrying in {nextRetryMs / 1_000}s.
                </p>
                <button
                  type="button"
                  onClick={() => wakeRetryRef.current?.()}
                  data-testid="add-asset-dialog-retry-now"
                  className="shrink-0 rounded bg-white/10 px-2 py-1 text-white/90 hover:bg-white/15"
                >
                  Retry now
                </button>
              </div>
            )}
          </div>
        )}

        {!draft ? (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-xs text-white/60">
              Category
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={busy}
                data-testid="add-asset-dialog-category"
                className="rounded-lg border border-white/15 bg-black/40 p-2 text-xs text-white/90 disabled:opacity-50"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-white/60">
              Describe it
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. a glowing crystal shard the player picks up"
                disabled={busy}
                data-testid="add-asset-dialog-description"
                className="h-20 resize-none rounded-lg border border-white/15 bg-black/40 p-2 text-xs text-white/90 placeholder:text-white/30 disabled:opacity-50"
              />
            </label>
            <button
              type="button"
              disabled={busy || !description.trim()}
              onClick={() => void onDraft()}
              data-testid="add-asset-dialog-draft"
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/15 disabled:cursor-default disabled:opacity-40"
            >
              Draft
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-xs text-white/60">
              Asset id
              <input
                value={draft.id}
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                disabled={busy}
                data-testid="add-asset-dialog-id"
                className="rounded-lg border border-white/15 bg-black/40 p-2 text-xs text-white/90 disabled:opacity-50"
              />
            </label>
            <p className="text-[11px] uppercase tracking-wide text-white/40">
              {draft.category} · {draft.targetSize}
            </p>
            <p className="text-xs text-white/70">{draft.prompt}</p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setDraft(null)}
                data-testid="add-asset-dialog-back"
                className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60 transition hover:bg-white/10 disabled:opacity-40"
              >
                Back
              </button>
              <button
                type="button"
                disabled={busy || !draft.id.trim()}
                onClick={() => void onAdd()}
                data-testid="add-asset-dialog-add"
                className="rounded-lg bg-crucible-accent px-3 py-1.5 text-xs font-medium text-black transition hover:brightness-110 disabled:cursor-default disabled:opacity-40"
              >
                {registering
                  ? 'Adding to Library…'
                  : registeredId
                    ? 'Generating first version…'
                    : 'Add to Library'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
